import { DqTable } from "@decentriq/components";
import { Stack, Tooltip, Typography } from "@mui/joy";
import { useMount } from "ahooks";
import { format } from "date-fns";
import { type MRT_ColumnDef } from "material-react-table";
import { type SnackbarKey } from "notistack";
import { memo, useCallback, useEffect, useMemo, useState } from "react";
import {
  mapMediaDataRoomErrorToSnackbar,
  useDataRoomSnackbar,
  useDebouncedSearch,
} from "hooks";
import MediaDataRoomLogsToolbar from "./MediaDataRoomLogsToolbar";
import { type StringifiedAuditLogRecord } from "./models";
import useMediaDataRoomAuditLogs from "./useMediaDataRoomAuditLogs";

const MediaDataRoomAuditLogs = memo(() => {
  const { enqueueSnackbar, closeSnackbar } = useDataRoomSnackbar();
  const { debouncedSearch, onSearchChange, searchValue } = useDebouncedSearch();
  const { data, refetch, isLoading, error } = useMediaDataRoomAuditLogs();
  const setErrorSnackbarId = useState<SnackbarKey | undefined>()[1];
  useMount(() => {
    void refetch();
  });
  useEffect(() => {
    if (error) {
      const snackbarId = enqueueSnackbar(
        ...mapMediaDataRoomErrorToSnackbar(
          error,
          "Audit log could not be retrieved."
        )
      );
      setErrorSnackbarId(snackbarId);
    } else {
      setErrorSnackbarId((snackbarId) => {
        if (snackbarId) {
          closeSnackbar(snackbarId);
        }
        return undefined;
      });
    }
  }, [enqueueSnackbar, closeSnackbar, setErrorSnackbarId, error]);
  const filteredData = useMemo<StringifiedAuditLogRecord[]>(
    () =>
      data?.logs
        .map(({ timestampMillis, user, details }) => ({
          details: details ? JSON.stringify(details, null, 2) : "",
          timestampMillis: timestampMillis
            ? format(new Date(Number(timestampMillis)), "HH:mm:ss dd-MM-yyyy")
            : "Invalid date",
          user: user || "Decentriq Platform",
        }))
        .filter((log) => {
          const trimmedSearchValue = debouncedSearch.trim();
          if (!trimmedSearchValue) return true;
          return (
            log.user.includes(trimmedSearchValue) ||
            log.details.includes(trimmedSearchValue)
          );
        }) ?? [],
    [data, debouncedSearch]
  );
  const getLogs = useCallback(() => data?.logs ?? [], [data?.logs]);
  const columns: MRT_ColumnDef<StringifiedAuditLogRecord>[] = [
    {
      accessorKey: "timestampMillis",
      grow: false,
      header: "Event date",
      id: "timestampMillis",
      size: 170,
    },
    {
      accessorKey: "user",
      header: "Performed by",
      id: "user",
      size: 200,
    },
    {
      Cell: ({ cell }) => {
        const logDetails =
          cell.getValue<StringifiedAuditLogRecord["details"]>();
        if (!logDetails) return null;
        return (
          <Tooltip
            placement="top-start"
            title={
              <Typography level="body-xs" textColor="white" whiteSpace="pre">
                {logDetails}
              </Typography>
            }
          >
            <Typography
              level="body-sm"
              overflow="hidden"
              textOverflow="ellipsis"
            >
              {logDetails}
            </Typography>
          </Tooltip>
        );
      },
      accessorKey: "details",
      header: "Details",
      id: "details",
      minSize: 600,
    },
  ];
  return (
    <Stack>
      <Stack alignItems="center" direction="row" justifyContent="space-between">
        <Typography level="body-md">
          The audit log tracks all operations by any participant in this data
          clean room.
        </Typography>
      </Stack>
      <DqTable
        columns={columns}
        data={filteredData}
        enableGlobalFilter={false}
        enableSorting={true}
        enableTopToolbar={true}
        initialState={{
          showGlobalFilter: true,
        }}
        muiTableHeadRowProps={{
          sx: {
            "& > .MuiTableCell-root:first-child": { pl: 2 },
            "& > .MuiTableCell-root:last-child": {
              "& .Mui-TableHeadCell-ResizeHandle-Wrapper": {
                right: "-1rem",
              },
              pr: 2,
            },
          },
        }}
        muiTablePaperProps={{
          sx: {
            display: "flex",
            flex: 1,
            flexDirection: "column",
            height: "100%",
            overflow: "hidden",
            width: "100%",
          },
        }}
        renderTopToolbarCustomActions={() => {
          return (
            <MediaDataRoomLogsToolbar
              getLogs={getLogs}
              hasData={filteredData.length > 0}
              onSearchChange={onSearchChange}
              searchValue={searchValue}
            />
          );
        }}
        state={{ isLoading }}
      />
    </Stack>
  );
});
MediaDataRoomAuditLogs.displayName = "MediaDataRoomAuditLogs";

export default MediaDataRoomAuditLogs;
